import axios from 'axios';
import type {
  DraftGenerationResponse,
  ApiResponse,
  ToneType,
  AttachmentData,
} from '../types/index';

// Extended request type for Phase 1 (includes email content)
export interface GenerateDraftRequest {
  emailId: string;
  emailFrom: string;
  emailSubject: string;
  emailBody: string;
  tone?: ToneType;
  templateId?: string;
  attachments?: AttachmentData[];
}

// API base URL - will be configured via environment
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://localhost:3001';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 120 second timeout for AI generation with attachments
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    // TODO: Add auth token from MSAL
    // const token = await getAccessToken();
    // config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.error?.message || 'An error occurred';
      return Promise.reject(new Error(message));
    } else if (error.request) {
      // Request made but no response
      return Promise.reject(new Error('Unable to connect to server'));
    } else {
      // Request setup error
      return Promise.reject(error);
    }
  }
);

// API functions

export async function generateDraft(
  request: GenerateDraftRequest
): Promise<DraftGenerationResponse> {
  const response = await api.post<ApiResponse<DraftGenerationResponse>>(
    '/api/drafts/generate',
    request
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error?.message || 'Failed to generate draft');
  }

  return response.data.data;
}

export async function healthCheck(): Promise<boolean> {
  try {
    const response = await api.get('/health');
    return response.status === 200;
  } catch {
    return false;
  }
}

export default api;
